import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { apiService } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import type {
  ContentRequestCreatePayload,
  ContentRequestPriority,
  SeriesScopeMode,
  TmdbEpisode,
  TmdbSearchResult,
  TmdbSeasonSummary
} from '../../types/contentRequest';
import { Clapperboard, Film, Loader2, Search, Star, Tv, X } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function buildSeriesNotes(opts: {
  mode: SeriesScopeMode;
  season?: number | null;
  episodes?: number[];
  overview?: string;
  userNotes?: string;
}) {
  const parts: string[] = [];
  if (opts.mode === 'full_series') parts.push('Pedido: série completa');
  if (opts.mode === 'season' && opts.season) parts.push(`Pedido: temporada ${opts.season} completa`);
  if (opts.mode === 'episodes' && opts.season && opts.episodes?.length) {
    const eps = [...opts.episodes].sort((a, b) => a - b).join(', ');
    parts.push(`Pedido: T${opts.season} episódios ${eps}`);
  }
  if (opts.userNotes?.trim()) parts.push(opts.userNotes.trim());
  else if (opts.overview?.trim()) parts.push(opts.overview.trim());
  return parts.join('\n\n');
}

const ContentRequestCreateModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const [step, setStep] = useState<'search' | 'confirm'>('search');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'multi' | 'movie' | 'series'>('multi');
  const [results, setResults] = useState<TmdbSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selected, setSelected] = useState<TmdbSearchResult | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [scopeMode, setScopeMode] = useState<SeriesScopeMode>('full_series');
  const [seasons, setSeasons] = useState<TmdbSeasonSummary[]>([]);
  const [pickedSeason, setPickedSeason] = useState<number | null>(null);
  const [episodes, setEpisodes] = useState<TmdbEpisode[]>([]);
  const [pickedEpisodes, setPickedEpisodes] = useState<number[]>([]);
  const [loadingSeason, setLoadingSeason] = useState(false);

  const [serverLabel, setServerLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<ContentRequestPriority>('medium');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const debouncedQuery = useDebounce(query, 450);

  useEffect(() => {
    if (!isOpen) return;
    setStep('search');
    setQuery('');
    setTypeFilter('multi');
    setResults([]);
    setSearching(false);
    setSearchError('');
    setSelected(null);
    setLoadingDetails(false);
    setScopeMode('full_series');
    setSeasons([]);
    setPickedSeason(null);
    setEpisodes([]);
    setPickedEpisodes([]);
    setServerLabel('');
    setNotes('');
    setPriority('medium');
    setError('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || step !== 'search') return;
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setResults([]);
      setSearchError('');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setSearching(true);
        setSearchError('');
        const res = await apiService.searchTmdbContent(q, typeFilter);
        if (cancelled) return;
        setResults(res.results || []);
        if (!(res.results || []).length) {
          setSearchError('Nenhum filme/série encontrado no TMDB.');
        }
      } catch (err: any) {
        if (cancelled) return;
        setResults([]);
        setSearchError(err?.response?.data?.error || err?.message || 'Falha na busca TMDB');
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, typeFilter, isOpen, step]);

  const pickResult = async (item: TmdbSearchResult) => {
    setError('');
    setLoadingDetails(true);
    setStep('confirm');
    setScopeMode('full_series');
    setPickedSeason(null);
    setEpisodes([]);
    setPickedEpisodes([]);
    setNotes('');
    try {
      const res = await apiService.getTmdbDetails(item.tmdbId, item.mediaType);
      const detail: TmdbSearchResult = { ...item, ...(res.data || {}) };
      setSelected(detail);
      setSeasons(detail.seasons || []);
    } catch {
      setSelected(item);
      setSeasons([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (!selected || selected.mediaType !== 'series') return;
    if (scopeMode === 'full_series') {
      setPickedSeason(null);
      setEpisodes([]);
      setPickedEpisodes([]);
      return;
    }
    if (!pickedSeason) return;
    if (scopeMode === 'season') {
      setEpisodes([]);
      setPickedEpisodes([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoadingSeason(true);
        const res = await apiService.getTmdbSeason(selected.tmdbId, pickedSeason);
        if (cancelled) return;
        setEpisodes(res.data?.episodes || []);
        setPickedEpisodes([]);
      } catch {
        if (!cancelled) setEpisodes([]);
      } finally {
        if (!cancelled) setLoadingSeason(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selected, scopeMode, pickedSeason]);

  const canSubmit = useMemo(() => {
    if (!selected?.title || !selected?.tmdbId) return false;
    if (selected.mediaType !== 'series') return true;
    if (scopeMode === 'full_series') return true;
    if (scopeMode === 'season') return Boolean(pickedSeason);
    if (scopeMode === 'episodes') return Boolean(pickedSeason && pickedEpisodes.length > 0);
    return false;
  }, [selected, scopeMode, pickedSeason, pickedEpisodes]);

  const toggleEpisode = (n: number) => {
    setPickedEpisodes((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort((a, b) => a - b)
    );
  };

  const selectAllEpisodes = () => {
    setPickedEpisodes(episodes.map((e) => e.episodeNumber));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !canSubmit) {
      setError('Complete a selecção do conteúdo');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const isSeries = selected.mediaType === 'series';
      const finalNotes = isSeries
        ? buildSeriesNotes({
            mode: scopeMode,
            season: pickedSeason,
            episodes: pickedEpisodes,
            overview: selected.overview,
            userNotes: notes
          })
        : (notes.trim() || selected.overview || undefined);

      const payload: ContentRequestCreatePayload = {
        title: selected.title,
        type: selected.mediaType,
        genre: selected.genre || undefined,
        serverLabel: serverLabel.trim() || undefined,
        notes: finalNotes,
        priority,
        coverUrl: selected.coverUrlLarge || selected.coverUrl || undefined,
        tmdbId: selected.tmdbId,
        season: isSeries && scopeMode !== 'full_series' ? pickedSeason : null,
        episode:
          isSeries && scopeMode === 'episodes' && pickedEpisodes.length === 1
            ? pickedEpisodes[0]
            : null
      };
      await apiService.createContentRequest(payload);
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Falha ao criar solicitação');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[min(96vw,56rem)] p-0 overflow-hidden gap-0">
        <DialogHeader className="border-b border-slate-200 bg-slate-900 px-6 py-5 text-white space-y-1">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-white/10 p-2.5">
                <Clapperboard className="h-5 w-5 text-amber-300" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-white text-lg">Nova solicitação</DialogTitle>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Máximo de 3 pedidos seus na lista. Votos em pedidos de outros são livres. Busca TMDB com capas oficiais.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-300 hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>

        {step === 'search' ? (
          <div className="space-y-5 p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nome do filme ou série (ex: Reacher)"
                  className="pl-9 h-11 text-sm"
                />
              </div>
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                {([
                  { id: 'multi', label: 'Todos' },
                  { id: 'movie', label: 'Filmes' },
                  { id: 'series', label: 'Séries' }
                ] as const).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTypeFilter(opt.id)}
                    className={`rounded-md px-3 py-2 text-xs font-medium transition ${
                      typeFilter === opt.id
                        ? 'bg-slate-900 text-white shadow'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {searching && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                A pesquisar no TMDB…
              </div>
            )}
            {searchError && !searching && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
                {searchError}
              </p>
            )}

            <div className="max-h-[52vh] overflow-y-auto pr-1">
              {results.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {results.map((item) => (
                    <button
                      key={`${item.mediaType}-${item.tmdbId}`}
                      type="button"
                      onClick={() => pickResult(item)}
                      className="group text-left rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md hover:border-slate-400 transition"
                    >
                      <div className="relative aspect-[2/3] bg-slate-100 overflow-hidden">
                        {item.coverUrl ? (
                          <img
                            src={item.coverUrl}
                            alt={item.title}
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-slate-400">
                            {item.mediaType === 'series' ? <Tv className="h-8 w-8" /> : <Film className="h-8 w-8" />}
                          </div>
                        )}
                        <span className="absolute left-2 top-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                          {item.mediaType === 'series' ? 'Série' : 'Filme'}
                        </span>
                        {item.rating != null && item.rating > 0 && (
                          <span className="absolute right-2 top-2 inline-flex items-center gap-0.5 rounded-md bg-amber-400/95 px-1.5 py-0.5 text-[10px] font-bold text-slate-900">
                            <Star className="h-3 w-3 fill-current" />
                            {item.rating}
                          </span>
                        )}
                      </div>
                      <div className="p-3 space-y-1">
                        <p className="text-sm font-semibold text-slate-900 line-clamp-2 leading-snug">{item.title}</p>
                        <p className="text-xs text-slate-500">{item.year || 'Ano n/d'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                !searching && (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 py-16 px-6 text-center">
                    <Search className="h-8 w-8 text-slate-300 mb-3" />
                    <p className="text-sm font-medium text-slate-700">Procure o título no TMDB</p>
                    <p className="mt-2 max-w-md text-xs text-slate-500 leading-relaxed">
                      Aparecem só filmes e séries (com capa). Depois escolhe se quer a série completa, uma temporada ou episódios específicos.
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid md:grid-cols-[240px_1fr]">
            <aside className="bg-slate-950 p-5 space-y-3 md:min-h-[460px]">
              <div className="overflow-hidden rounded-xl border border-white/10 shadow-lg bg-slate-900">
                {selected?.coverUrlLarge || selected?.coverUrl ? (
                  <img
                    src={selected.coverUrlLarge || selected.coverUrl || ''}
                    alt={selected?.title}
                    className="w-full aspect-[2/3] object-cover"
                  />
                ) : (
                  <div className="aspect-[2/3] flex items-center justify-center text-slate-500">
                    <Film className="h-10 w-10" />
                  </div>
                )}
              </div>
              <button
                type="button"
                className="w-full text-xs text-slate-300 underline-offset-2 hover:underline py-1"
                onClick={() => setStep('search')}
              >
                ← Trocar conteúdo
              </button>
            </aside>

            <div className="space-y-4 p-6">
              {loadingDetails ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-8">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  A carregar detalhes TMDB…
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-slate-900 leading-tight">{selected?.title}</h3>
                    <p className="text-sm text-slate-500">
                      {selected?.mediaType === 'series' ? 'Série' : 'Filme'}
                      {selected?.year ? ` · ${selected.year}` : ''}
                      {selected?.rating ? ` · ★ ${selected.rating}` : ''}
                      {selected?.genre ? ` · ${selected.genre}` : ''}
                      {selected?.imdbId ? ` · ${selected.imdbId}` : ''}
                    </p>
                    {selected?.overview ? (
                      <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{selected.overview}</p>
                    ) : null}
                  </div>

                  {selected?.mediaType === 'series' && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">O que pretende pedir?</p>
                      <div className="grid sm:grid-cols-3 gap-2">
                        {([
                          { id: 'full_series', label: 'Série completa' },
                          { id: 'season', label: 'Uma temporada' },
                          { id: 'episodes', label: 'Episódios' }
                        ] as const).map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => {
                              setScopeMode(opt.id);
                              if (opt.id === 'full_series') setPickedSeason(null);
                            }}
                            className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition ${
                              scopeMode === opt.id
                                ? 'border-slate-900 bg-slate-900 text-white'
                                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      {(scopeMode === 'season' || scopeMode === 'episodes') && (
                        <div className="space-y-2 pt-1">
                          <label className="text-xs font-medium text-slate-600">Temporada</label>
                          <div className="flex flex-wrap gap-2">
                            {seasons.map((s) => (
                              <button
                                key={s.seasonNumber}
                                type="button"
                                onClick={() => setPickedSeason(s.seasonNumber)}
                                className={`rounded-lg border px-3 py-2 text-xs font-medium ${
                                  pickedSeason === s.seasonNumber
                                    ? 'border-slate-900 bg-slate-900 text-white'
                                    : 'border-slate-200 bg-white text-slate-700'
                                }`}
                              >
                                T{s.seasonNumber}
                                <span className="ml-1 opacity-70">({s.episodeCount} ep)</span>
                              </button>
                            ))}
                            {!seasons.length && (
                              <p className="text-xs text-slate-500">Sem temporadas listadas no TMDB.</p>
                            )}
                          </div>
                        </div>
                      )}

                      {scopeMode === 'episodes' && pickedSeason && (
                        <div className="space-y-2 pt-1">
                          <div className="flex items-center justify-between gap-2">
                            <label className="text-xs font-medium text-slate-600">Episódios</label>
                            <button
                              type="button"
                              className="text-xs text-slate-600 underline"
                              onClick={selectAllEpisodes}
                              disabled={!episodes.length}
                            >
                              Seleccionar todos
                            </button>
                          </div>
                          {loadingSeason ? (
                            <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />A carregar episódios…
                            </div>
                          ) : (
                            <div className="max-h-40 overflow-y-auto grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                              {episodes.map((ep) => {
                                const on = pickedEpisodes.includes(ep.episodeNumber);
                                return (
                                  <button
                                    key={ep.episodeNumber}
                                    type="button"
                                    title={ep.name}
                                    onClick={() => toggleEpisode(ep.episodeNumber)}
                                    className={`rounded-md border px-2 py-1.5 text-xs font-medium ${
                                      on
                                        ? 'border-emerald-600 bg-emerald-600 text-white'
                                        : 'border-slate-200 bg-white text-slate-700'
                                    }`}
                                  >
                                    E{String(ep.episodeNumber).padStart(2, '0')}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {pickedEpisodes.length > 0 && (
                            <p className="text-xs text-slate-500">
                              Seleccionados: {pickedEpisodes.map((n) => `E${n}`).join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-600">Prioridade</label>
                      <select
                        className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm"
                        value={priority}
                        onChange={(e) => setPriority(e.target.value as ContentRequestPriority)}
                      >
                        <option value="low">Baixa</option>
                        <option value="medium">Média</option>
                        <option value="high">Alta</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-600">Servidor / painel</label>
                      <Input
                        value={serverLabel}
                        onChange={(e) => setServerLabel(e.target.value)}
                        placeholder="ex: Union"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-600">Notas (opcional)</label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="Ex.: áudio PT-BR, qualidade 1080p…"
                    />
                  </div>

                  {error && <p className="text-sm text-red-600">{error}</p>}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={isLoading || !canSubmit}>
                      {isLoading ? 'A guardar…' : 'Criar solicitação'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContentRequestCreateModal;
